// Shared number/money/label formatters for compact UI surfaces (list rows,
// dropdown options, badges). For long-form locale-aware money rendering
// with currency + cents, use fmtMoney inside customer-detail.tsx.

import type { LineStage } from '@/lib/api';

/**
 * Compact USD formatter — `$580`, `$1.2K`, `$12K`, `$1.2M`. Returns `—`
 * for zero so empty cells don't read as legitimate zero-dollar values.
 *
 * Input is in cents (matches the bigint cents storage convention).
 */
export function fmtCompactUsd(cents: number): string {
  if (cents === 0) return '—';
  const abs = Math.abs(cents / 100);
  if (abs >= 1_000_000) return `$${(abs / 1_000_000).toFixed(1)}M`;
  if (abs >= 10_000) return `$${(abs / 1000).toFixed(0)}K`;
  if (abs >= 1000) return `$${(abs / 1000).toFixed(1)}K`;
  return `$${abs.toFixed(0)}`;
}

/**
 * Human labels for the line-item pipeline stages.
 *
 * Lives here rather than in a component because the RFQ list, the RFQ row and
 * the stage filter all need it. (There are three further copies in
 * rfq-detail.tsx, customer-detail.tsx and orders.tsx — consolidating those is
 * worthwhile but out of scope for the priority board.)
 */
export const STAGE_LABEL: Record<LineStage, string> = {
  received: 'Received',
  sourcing: 'Sourcing',
  quoted: 'Quoted',
  accepted: 'Accepted',
  po_sent: 'PO sent',
  invoiced: 'Invoiced',
  paid: 'Paid',
  shipped: 'Shipped',
  delivered: 'Delivered',
  reviewed: 'Reviewed',
  no_quote: 'No quote',
  lost: 'Lost',
};

/** "just now" / "12m ago" / "3h ago" / "5d ago" / a locale date beyond 30 days. */
export function timeAgo(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const min = Math.floor(ms / 60_000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const d = Math.floor(hr / 24);
  if (d < 30) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
}
